import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';

interface MemberConfirmationProps {
  member: {
    id: string;
    name: string;
    email: string;
    current_status: string;
    season?: Record<string, {
      status?: string;
      handicap?: number;
    }>;
  };
  onConfirm: (memberId: string, data: { join2025: boolean; isVietnamese: boolean; notes: string; membershipType: string | null }) => void;
  onCancel?: () => void;
}

export function MemberConfirmation({ member, onConfirm, onCancel }: MemberConfirmationProps) {
  const [join2025, setJoin2025] = useState(false);
  const [isVietnamese, setIsVietnamese] = useState(false);
  const [membershipType, setMembershipType] = useState<'student' | 'single' | 'family' | null>(null);
  const [notes, setNotes] = useState('');
  const [email, setEmail] = useState(member.email || '');
  const [emailError, setEmailError] = useState<string | null>(
    !member.email ? 'Email is required' : null
  );
  const currentSeason = member.season?.['2025'];
  const isInactive = member.current_status === 'inactive';
  
  const validateEmail = (email: string) => {
    if (!email.trim()) return 'Email is required';
    // Simplified email regex that matches common email formats while being less restrictive
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      return 'Invalid email format';
    }
    return null;
  };

  const handleConfirm = () => {
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }

    console.log('Confirming member with data:', {
      memberId: member.id,
      join2025,
      isVietnamese,
      membershipType,
      notes,
      email
    });
    onConfirm(member.id, { 
      join2025, 
      isVietnamese,
      membershipType,
      notes,
      email
    });
  };
  
  return (
    <div className="max-h-[80vh] overflow-y-auto">
      <h2 className="text-xl font-semibold mb-4">Existing Member Found</h2>
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <div className="space-y-2">
          <div className="space-y-4">
            <p className="text-sm">
              <span className="font-medium">Name:</span> {member.name}
            </p>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError(validateEmail(e.target.value));
                }}
                className={`mt-1 block w-full rounded-md shadow-sm ${
                  emailError ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
                }`}
                required
              />
              {emailError && (
                <p className="mt-1 text-sm text-red-600">{emailError}</p>
              )}
            </div>
          </div>
          {currentSeason && (
            <>
              <p className="text-sm">
                <span className="font-medium">Status:</span>{' '}
                {currentSeason.status === 'M' ? 'Member' : 
                 currentSeason.status === 'NVM' ? 'Non-Voting Member' : 
                 'Inactive'}
              </p>
              {currentSeason.handicap && (
                <p className="text-sm">
                  <span className="font-medium">Handicap:</span> {currentSeason.handicap}
                </p>
              )}
            </>
          )}
        </div>
      </div>
      
      {isInactive && (
        <div className="space-y-4 mb-6">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="join2025"
                type="checkbox"
                checked={join2025}
                onChange={(e) => setJoin2025(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="join2025" className="font-medium text-gray-700">
                Join 2025 Membership
              </label>
              <p className="text-gray-500">
                Sign up for the 2025 season (Nov '24 - May '25)
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="isVietnamese"
                type="checkbox"
                checked={isVietnamese}
                onChange={(e) => setIsVietnamese(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="isVietnamese" className="font-medium text-gray-700">
                Vietnamese Heritage
              </label>
              <p className="text-gray-500">
                Check if you have Vietnamese heritage
              </p>
            </div>
          </div>

          {join2025 && (
            <div className="space-y-3 mb-6">
              <label className="block text-sm font-medium text-gray-700">
                Membership Type
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="membershipType"
                    value="student"
                    checked={membershipType === 'student'}
                    onChange={(e) => setMembershipType(e.target.checked ? 'student' : null)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Student Membership - $100</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="membershipType"
                    value="single"
                    checked={membershipType === 'single'}
                    onChange={(e) => setMembershipType(e.target.checked ? 'single' : null)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Single Membership - $150</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="membershipType"
                    value="family"
                    checked={membershipType === 'family'}
                    onChange={(e) => setMembershipType(e.target.checked ? 'family' : null)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Family Membership - $250</span>
                </label>
              </div>
            </div>
          )}
        </div>
      )}
      
      <div className="mb-6">
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
          Notes
        </label>
        <textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={1000}
          rows={4}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm resize-y mt-1"
          placeholder="Enter any notes, pairing preferences or requests..."
        />
        <p className="mt-1 text-xs text-gray-500">
          {notes.length}/1000 characters
        </p>
      </div>

      <div className="flex justify-end gap-3">
        {onCancel && (
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleConfirm}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Confirm & Continue
        </button>
      </div>
    </div>
  );
}